import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import type { User, UserState, Permission } from "../types/types";
import { useSelector } from "react-redux";
import fetching from "../helpers/fetching/fetching";
import fireAlert from "../helpers/alerts/fireAlert";

type RequiredPermission = {
  path: string;
  method: string;
  scope: string;
  model: string;
};

type UsePermissionsReturn = {
  permissions: Permission[];
  userData: User | null;
  loading: boolean;
  error: string | null;
  hasPermission: (requiredPermissions: RequiredPermission[]) => boolean;
  getPermissionIdsByCriteria: (criteria: RequiredPermission) => string[];
  isDataReady: boolean;
  refreshPermissions: () => void;
  refreshUserData: () => void;
};

type GetPermissions = (query?: string) => Promise<any>;
type GetAllPermissions = () => Promise<any>;

export const getPermissions: GetPermissions = async (query = "") => {
  try {
    const res = await fetching(`/permissions?${query ? query : ""}`);
    if (res.status !== 200) throw res;
    return await res.json();
  } catch (error) {
    throw error;
  }
};

export const getAllPermissions: GetAllPermissions = async () => {
  try {
    const res = await fetching(`/permissions?all`);
    if (res.status !== 200) throw res;
    const data = await res.json();
    return data.data;
  } catch (error) {
    throw error;
  }
};

const getCurrentUser = async (userToken: string): Promise<any> => {
  try {
    const res = await fetching(`/me`, userToken, "GET");
    if (res.status !== 200) throw res;
    const data = await res.json();
    return data;
  } catch (error) {
    throw error;
  }
};

export const usePermissions = (): UsePermissionsReturn => {
  // Fetch user data from /me endpoint with 1-hour caching
  const userToken = useSelector((state: UserState) => state.user.userToken);
  const {
    data: userData = null,
    isLoading: userLoading,
    error: userError,
    refetch: refreshUserData,
  } = useQuery({
    queryKey: ["me", userToken],
    queryFn: () => getCurrentUser(userToken),
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 30,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Use React Query for fetching and caching permissions
  const {
    data: permissions = [],
    isLoading: permissionsLoading,
    error: queryError,
    refetch: refreshPermissions,
  } = useQuery({
    queryKey: ["permissions"],
    queryFn: getAllPermissions,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000, // 1 hour (formerly cacheTime)
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Check if data is ready for permission checking
  const isDataReady =
    !userLoading && !permissionsLoading && !!userData && !!permissions.length;

  const getPermissionIdsByCriteria = useCallback(
    (criteria: RequiredPermission): string[] => {
      if (!permissions.length) {
        return [];
      }

      return permissions
        .filter(
          (permission: Permission) =>
            permission.path === criteria.path &&
            permission.method === criteria.method &&
            permission.scope === criteria.scope &&
            permission.model === criteria.model
        )
        .map((permission: Permission) => permission.id);
    },
    [permissions]
  );

  const hasPermission = useCallback(
    (requiredPermissions: RequiredPermission[]): boolean => {
      // Return false if data is still loading or not available
      if (!isDataReady) {
        return false;
      }

      if (!userData?.permissions?.length || !permissions.length) {
        return false;
      }

      // Check if user has all required permissions
      return requiredPermissions.every((required) => {
        // Find permission IDs that match the required criteria
        const matchingPermissionIds = getPermissionIdsByCriteria(required);

        // Check if user has any of the matching permission IDs
        return matchingPermissionIds.some((permissionId: string) =>
          userData.permissions.includes(permissionId)
        );
      });
    },
    [userData, permissions, getPermissionIdsByCriteria, isDataReady]
  );

  return {
    permissions,
    userData,
    loading: userLoading || permissionsLoading,
    error: userError?.message || queryError?.message || null,
    hasPermission,
    getPermissionIdsByCriteria,
    isDataReady,
    refreshPermissions,
    refreshUserData,
  };
};
